// What a Bitcoin address or transaction hash looks like, and whether a given
// one is real.
//
// Two levels of check, because two places need different things:
//
//   idShapeOf(id)      pattern only. Is this the right length and alphabet to be
//                      an address or a transaction hash? Cheap, and all the
//                      router and the edge function's 404 need.
//   verifyId(id)       also checks the address's own checksum. Every Bitcoin
//                      address carries one precisely so that a typo cannot be
//                      mistaken for somebody else's address.
//
// The shape test alone let /btc-explorer/1A1zP1eP5QGefi2DMPTfTL5SLmv7Divfaa -
// Satoshi's address with the last two characters swapped - through to
// blockchain.info, which answered 400, and the page said "No results" as though
// the address merely had no history. The checksum catches that here, with no
// request, and the page can say it is a typo.
//
// Imports nothing, so the browser, the Netlify edge function and Node scripts
// can all share it - the same reason txValue.js and exchangeFlow.js stand alone.
// That includes SHA-256, which is written out below rather than taken from Web
// Crypto: `crypto.subtle` is asynchronous, absent from jsdom so the tests could
// not exercise this, and absent again on an insecure origin. Sixty lines of
// arithmetic buys a check that behaves the same everywhere.

// A transaction hash is 64 hex characters. It carries no checksum of its own -
// it is a hash - so shape is all there is to check.
export const TX_HASH = /^[a-f0-9]{64}$/i;

// Base58 (legacy, 1 or 3 prefixed) or bech32 (bc1). Base58 leaves out 0, O, I
// and l, which is why the character class looks hand-picked; bech32 is lower
// case and leaves out 1, b, i and o.
export const BTC_ADDRESS = /^(?:[13][a-km-zA-HJ-NP-Z1-9]{25,34}|bc1[ac-hj-np-z02-9]{8,87})$/;

// "transaction", "wallet", or null for something that is neither. Used for
// routing and for the edge function's 404, both of which have to decide before
// anything can be awaited.
export const idShapeOf = (id) => {
  if (typeof id !== "string") return null;
  if (TX_HASH.test(id)) return "transaction";
  if (BTC_ADDRESS.test(id)) return "wallet";
  return null;
};

// ------------------------------------------------------------------- base58check

const BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

// Base 58 to bytes, by long division. No BigInt: it would work, but this runs
// in the browser on a string the visitor typed and the loop is 25 bytes long.
const base58ToBytes = (address) => {
  const bytes = [0];
  for (const character of address) {
    const value = BASE58.indexOf(character);
    if (value === -1) return null;
    let carry = value;
    for (let i = 0; i < bytes.length; i += 1) {
      carry += bytes[i] * 58;
      bytes[i] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  // each leading "1" is a leading zero byte, which the arithmetic above drops
  for (const character of address) {
    if (character !== "1") break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
};

// SHA-256, FIPS 180-4. The first 32 bits of the fractional parts of the cube
// roots of the first 64 primes, and the square roots of the first 8, exactly as
// the standard specifies them.
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const rotr = (value, bits) => (value >>> bits) | (value << (32 - bits));

const sha256 = (bytes) => {
  const h = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ];

  // the message, a 1 bit, zeroes, then its length in bits as 64 bits
  const bitLength = bytes.length * 8;
  const padded = new Uint8Array(Math.ceil((bytes.length + 9) / 64) * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  new DataView(padded.buffer).setUint32(padded.length - 4, bitLength);

  const w = new Uint32Array(64);
  for (let chunk = 0; chunk < padded.length; chunk += 64) {
    const view = new DataView(padded.buffer, chunk, 64);
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(i * 4);
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i += 1) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (hh + s1 + ch + K[i] + w[i]) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    [a, b, c, d, e, f, g, hh].forEach((value, i) => {
      h[i] = (h[i] + value) >>> 0;
    });
  }

  const digest = new Uint8Array(32);
  const view = new DataView(digest.buffer);
  h.forEach((word, i) => view.setUint32(i * 4, word));
  return digest;
};

const verifyBase58Check = (address) => {
  const bytes = base58ToBytes(address);
  // 1 version byte + a 20 byte hash + 4 checksum bytes. Nothing else is a
  // P2PKH or P2SH address, whatever its length made it look like.
  if (!bytes || bytes.length !== 25) return false;
  // 0x00 pays to a public key hash (1…), 0x05 to a script hash (3…)
  if (bytes[0] !== 0x00 && bytes[0] !== 0x05) return false;

  const payload = bytes.slice(0, 21);
  const checksum = bytes.slice(21);
  const digest = sha256(sha256(payload));
  return checksum.every((byte, i) => byte === digest[i]);
};

// ------------------------------------------------------------------------ bech32
//
// BIP-173 for witness version 0 (bc1q…, and the long bc1q… for P2WSH) and
// BIP-350 for version 1 and up (bc1p…, taproot). The two differ only in the
// constant the checksum comes out to, which is the whole reason BIP-350 exists:
// bech32's original constant let a final character be inserted or removed
// without the checksum noticing.

const BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
const BECH32_CONST = 1;
const BECH32M_CONST = 0x2bc830a3;

const polymod = (values) => {
  const GENERATOR = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let checksum = 1;
  for (const value of values) {
    const top = checksum >>> 25;
    checksum = ((checksum & 0x1ffffff) << 5) ^ value;
    for (let i = 0; i < 5; i += 1) {
      if ((top >>> i) & 1) checksum ^= GENERATOR[i];
    }
  }
  return checksum >>> 0;
};

// "bc" expanded the way the checksum wants it: high bits, a separator, low bits.
const HRP_EXPANDED = [
  "b".charCodeAt(0) >> 5,
  "c".charCodeAt(0) >> 5,
  0,
  "b".charCodeAt(0) & 31,
  "c".charCodeAt(0) & 31,
];

// 5-bit groups back to bytes, which is how the witness program's length - and
// so which kind of output it pays to - is known.
const fromWords = (words) => {
  let accumulator = 0;
  let bits = 0;
  const bytes = [];
  for (const word of words) {
    accumulator = (accumulator << 5) | word;
    bits += 5;
    while (bits >= 8) {
      bits -= 8;
      bytes.push((accumulator >> bits) & 0xff);
    }
  }
  // whatever is left must be padding: fewer than 5 bits, all of them zero
  if (bits >= 5 || ((accumulator << (8 - bits)) & 0xff) !== 0) return null;
  return bytes;
};

const verifyBech32 = (address) => {
  // Mixed case is not allowed, and the regex has already ruled out upper case.
  const body = address.slice(3);
  const words = [];
  for (const character of body) {
    const value = BECH32.indexOf(character);
    if (value === -1) return false;
    words.push(value);
  }
  // the last six characters are the checksum
  if (words.length < 7) return false;

  const version = words[0];
  if (version > 16) return false;

  const expected = version === 0 ? BECH32_CONST : BECH32M_CONST;
  if (polymod([...HRP_EXPANDED, ...words]) !== expected) return false;

  const program = fromWords(words.slice(1, -6));
  if (!program) return false;
  // Witness programs run 2 to 40 bytes; version 0 is defined for exactly 20
  // (a key hash) or 32 (a script hash), and nothing else will ever be paid.
  if (program.length < 2 || program.length > 40) return false;
  if (version === 0 && program.length !== 20 && program.length !== 32) return false;
  return true;
};

// ---------------------------------------------------------------------- the check

// Whether an id is real: the right shape, and for an address, one whose own
// checksum agrees. A transaction hash has nothing to check beyond its shape.
//
// Returns "transaction", "wallet", or null.
export const verifyId = (id) => {
  const shape = idShapeOf(id);
  if (shape !== "wallet") return shape;
  const valid = id.startsWith("bc1") ? verifyBech32(id) : verifyBase58Check(id);
  return valid ? "wallet" : null;
};
